<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-chart-area me-2" style="color: #3b82f6;"></i>{{ $t('Sales Summary') }}
          </h5>
          <small class="text-muted">{{ $t('Sales') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3" v-for="kpi in salesKPIs" :key="kpi.key">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t(kpi.label) }}</div>
            <div class="fw-bold fs-4" :class="kpi.colorClass">{{ kpi.value }}</div>
            <div class="small text-muted mt-1">{{ kpi.sub }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Daily Sales Chart -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-chart-area me-2 text-primary"></i>{{ $t('Daily Sales') }}</h6>
      </div>
      <div class="card-body pt-0">
        <apexchart ref="salesChart" type="area" height="300" :options="salesChartOptions" :series="salesChartSeries" />
      </div>
    </div>

    <!-- Daily Breakdown Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-table me-2 text-secondary"></i>{{ $t('Daily Breakdown') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Date') }}</th>
                <th class="text-end">{{ $t('Orders') }}</th>
                <th class="text-end">{{ $t('Gross Sales') }}</th>
                <th class="text-end">{{ $t('Discount') }}</th>
                <th class="text-end">{{ $t('Net Sales') }}</th>
                <th class="text-end pe-3">{{ $t('VAT') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!salesTableData.length && !loading">
                <td colspan="6" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="row in salesTableData" :key="row.date">
                <td class="ps-3">{{ row.date }}</td>
                <td class="text-end">{{ row.orders }}</td>
                <td class="text-end">{{ formatCurrency(row.gross) }}</td>
                <td class="text-end text-danger">-{{ formatCurrency(row.discount) }}</td>
                <td class="text-end fw-semibold">{{ formatCurrency(row.net) }}</td>
                <td class="text-end pe-3 text-muted">{{ formatCurrency(row.vat) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="salesTableData.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3">{{ $t('Total') }}</td>
                <td class="text-end">{{ salesTotals.orders }}</td>
                <td class="text-end">{{ formatCurrency(salesTotals.gross) }}</td>
                <td class="text-end text-danger">-{{ formatCurrency(salesTotals.discount) }}</td>
                <td class="text-end">{{ formatCurrency(salesTotals.net) }}</td>
                <td class="text-end pe-3">{{ formatCurrency(salesTotals.vat) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <!-- Loading spinner -->
    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportSalesSummary',
  components: { apexchart: VueApexCharts },
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Sales Summary',
      summary: null,
      daily: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.salesSummary(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.summary  = payload.summary || null;
      this.daily    = payload.daily   || [];
    },

    async buildPdfDefinition() {
      await this.$nextTick();
      const chartImg = await this.getChartImage('salesChart');
      const k = this.salesKPIs;
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Sales Summary'), style: 'sectionTitle' },
        {
          columns: k.map(kpi => ({
            text: [
              { text: this.$t(kpi.label) + '\n', style: 'kpiLabel' },
              { text: String(kpi.value), style: 'kpiValue' },
            ],
            width: '*',
          })),
          margin: [0, 0, 0, 16],
        },
      ];
      if (chartImg) {
        content.push(
          { text: this.$t('Daily Sales'), style: 'sectionTitle' },
          { image: chartImg, width: 500, margin: [0, 0, 0, 12] },
        );
      }
      content.push(
        { text: this.$t('Daily Breakdown'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('Date'), style: 'tableHeader' },
                { text: this.$t('Orders'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Gross Sales'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Discount'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Net Sales'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('VAT'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.salesTableData.map(r => [
                { text: r.date, style: 'tableCell' },
                { text: String(r.orders), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(r.gross), style: 'tableCell', alignment: 'right' },
                { text: `-${this.formatCurrencyRaw(r.discount)}`, style: 'tableCell', alignment: 'right', color: '#ef4444' },
                { text: this.formatCurrencyRaw(r.net), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(r.vat), style: 'tableCell', alignment: 'right' },
              ]),
              [
                { text: this.$t('Total'), style: 'tableHeader' },
                { text: String(this.salesTotals.orders), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.salesTotals.gross), style: 'tableHeader', alignment: 'right' },
                { text: `-${this.formatCurrencyRaw(this.salesTotals.discount)}`, style: 'tableHeader', alignment: 'right', color: '#ef4444' },
                { text: this.formatCurrencyRaw(this.salesTotals.net), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.salesTotals.vat), style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      );
      return { content };
    },
  },
  computed: {
    salesTableData() {
      return this.daily || [];
    },
    salesTotals() {
      return this.salesTableData.reduce((acc, r) => ({
        orders: acc.orders + (r.orders || 0),
        gross: acc.gross + (r.gross || 0),
        discount: acc.discount + (r.discount || 0),
        net: acc.net + (r.net || 0),
        vat: acc.vat + (r.vat || 0),
      }), { orders: 0, gross: 0, discount: 0, net: 0, vat: 0 });
    },
    salesKPIs() {
      const s = this.summary || {};
      const days = s.days_in_range || this.salesTableData.length || 0;
      const avgPerDay = days > 0 ? (s.total_orders || 0) / days : 0;
      return [
        { key: 'gross',     label: 'Gross Sales',  value: this.formatCurrency(s.gross_sales),  sub: `${days} ${this.$t('days')}`, colorClass: 'text-primary' },
        { key: 'net',       label: 'Net Sales',    value: this.formatCurrency(s.net_sales),    sub: `${days} ${this.$t('days')}`, colorClass: 'text-success' },
        { key: 'orders',    label: 'Total Orders', value: String(s.total_orders || 0),         sub: `${avgPerDay.toFixed(1)} ${this.$t('avg/day')}`, colorClass: '' },
        { key: 'cancelled', label: 'Cancelled',    value: String(s.cancelled_count || 0),      sub: `${(s.cancelled_percent || 0).toFixed(1)}%`, colorClass: 'text-danger' },
      ];
    },
    salesChartSeries() {
      return [
        { name: this.$t('Gross Sales'), data: this.salesTableData.map(r => r.gross || 0) },
        { name: this.$t('Net Sales'),   data: this.salesTableData.map(r => r.net   || 0) },
      ];
    },
    salesChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Sarabun, sans-serif' },
        xaxis: { categories: this.salesTableData.map(r => r.date) },
        yaxis: { labels: { formatter: v => this.formatCompact(v) } },
        colors: ['#3b82f6', '#22c55e'],
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'right' },
        tooltip: { y: { formatter: v => this.formatCurrency(v) } },
        grid: { borderColor: '#f1f5f9' },
      };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
